// =====================================================================
// nen.ts — tải dữ liệu nền của hệ thống thật (N5, SKILL §5):
//   * Chưa có cache ⇒ rpc_tai_nen() — 1 lượt cho mọi thứ màn cần. Có cache (IndexedDB) ⇒ màn vẽ NGAY từ cache, nền gọi
//     rpc_tai_nen(since) rồi vá vào (core/nen.ts). Vá xong lệch số dòng server ⇒ tải lại cả bộ.
//   * Lớp lưu trữ (QĐ-H49) và ca Hoàn thành theo tháng (QĐ-H95) tải riêng, chỉ khi mở đúng chỗ đó.
//   * Gọi PostgREST bằng fetch để đo đúng: tổng · máy chủ (DB tự báo) · mạng · byte (SKILL §6).
// Mọi màn đọc nền qua useNen(userId) — TanStack Query gộp các lời gọi cùng khoá thành 1.
// =====================================================================
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { type NhomManHinh, ghiDiemDo } from "../core/perf";
import { gopDeltaNen, type GoiNen, type KhoiNen, vaGoi } from "../core/nen";
import { type KetQua, thanhCong, thatBai } from "./ketQua";
import { queryClient } from "./queryClient";
import { supabase } from "./supabaseClient";

const URL_GOC = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
const ANON = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? "";

/** Lùi mốc delta 5 phút: dòng của transaction ghi updated_at TRƯỚC lúc commit vẫn được lấy lại. Vá trùng không sao. */
const LUI_MOC_MS = 5 * 60_000;

export const khoaNen = (userId: string) => ["nen", userId] as const;
export const khoaLuuTru = (userId: string) => ["luu_tru", userId] as const;
export const khoaNghiXong = (userId: string, thang: string) => ["nghi_xong", userId, thang] as const;

async function goiDoc<T extends { may_chu_ms?: number }>(ten: string, body: Record<string, unknown>, nhom: NhomManHinh, demDong?: (t: T) => number): Promise<KetQua<T>> {
  const t0 = performance.now();
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return thatBai("Phiên đăng nhập đã hết — tải lại trang để đăng nhập lại");
    const url = `${URL_GOC}/rest/v1/rpc/${ten}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { apikey: ANON, Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    const buf = await res.arrayBuffer();
    const text = new TextDecoder().decode(buf);
    if (!res.ok) {
      let msg = text;
      try { msg = (JSON.parse(text) as { message?: string }).message ?? text; } catch { /* giữ nguyên chữ */ }
      return thatBai(msg || `${ten} lỗi ${res.status}`);
    }
    const kq = JSON.parse(text) as T;
    const tong = performance.now() - t0;
    const mayChu = Number(kq.may_chu_ms) || 0;
    const nen = (performance.getEntriesByName(url).pop() as PerformanceResourceTiming | undefined)?.encodedBodySize || undefined;
    ghiDiemDo({ man_hinh: nhom, buoc: ten, ms: tong, byte: buf.byteLength, so_dong: demDong?.(kq), cache_hit: false });
    if (mayChu) {
      ghiDiemDo({ man_hinh: nhom, buoc: ten + "_may_chu", ms: mayChu });
      ghiDiemDo({ man_hinh: nhom, buoc: ten + "_mang", ms: Math.max(0, tong - mayChu), byte: nen });
    }
    return thanhCong(kq);
  } catch (e) {
    return thatBai(e instanceof TypeError ? "Mất kết nối mạng — đang dùng dữ liệu đã lưu trên máy." : e);
  }
}

const demGoi = (g: { bang: Record<string, KhoiNen> }) => Object.values(g.bang).reduce((s, k) => s + k.rows.length + (k.xoa?.length ?? 0), 0);

export async function taiNen(cu: GoiNen | undefined): Promise<GoiNen> {
  if (cu?.luc) {
    const since = new Date(new Date(cu.luc).getTime() - LUI_MOC_MS).toISOString();
    const d = await goiDoc<GoiNen>("rpc_tai_nen", { p_since: since }, "khoi_dong", demGoi);
    if (!d.ok) throw new Error(d.error);
    const g = gopDeltaNen(cu, d.data);
    ghiDiemDo({ man_hinh: "khoi_dong", buoc: "delta_vao_cache", ms: 0, so_dong: g.soDongDoi });
    if (g.khop) return g.goi;
    // cache không còn khớp server ⇒ tải lại cả bộ (hiếm: dòng bị dọn hẳn, server đổi cột)
  }
  const b = await goiDoc<GoiNen>("rpc_tai_nen", {}, "khoi_dong", demGoi);
  if (!b.ok) throw new Error(b.error);
  return b.data;
}

export function useNen(userId: string) {
  const queryKey = khoaNen(userId);
  return useQuery<GoiNen>({
    queryKey,
    queryFn: ({ client }) => taiNen(client.getQueryData<GoiNen>(queryKey)),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    // Gói vài trăm KB: so sâu từng phần tử tốn hơn cả tải lại — core/nen.ts tự giữ object khối không đổi.
    structuralSharing: false,
    retry: 1,
  });
}

/** Lớp "Toàn bộ lưu trữ" — chỉ tải khi `bat` (mở tab đó). */
export function useLopLuuTru(userId: string, bat: boolean) {
  return useQuery({
    queryKey: khoaLuuTru(userId),
    queryFn: async () => {
      const k = await goiDoc<GoiNen>("rpc_lop_luu_tru", {}, "lop", demGoi);
      if (!k.ok) throw new Error(k.error);
      return k.data;
    },
    enabled: bat,
    staleTime: 5 * 60_000,
    structuralSharing: false,
  });
}

export interface GoiNghiXongThang { thang: string; cols: string[]; rows: unknown[][] }

/** Ca Hoàn thành của 1 tháng ('YYYY-MM') — QĐ-H95: chọn tháng rồi mới tải. */
export function useNghiXongThang(userId: string, thang: string | null) {
  return useQuery({
    queryKey: khoaNghiXong(userId, thang ?? ""),
    queryFn: async () => {
      const k = await goiDoc<GoiNghiXongThang & { may_chu_ms?: number }>("rpc_nghi_xong_thang", { p_thang: thang }, "nghiday", (x) => x.rows.length);
      if (!k.ok) throw new Error(k.error);
      return k.data;
    },
    enabled: !!thang,
    staleTime: 60_000,
  });
}

/** Hàm ghi trả khối (quy ước 0012) ⇒ vá thẳng vào cache nền, không tải lại. */
export function vaCacheNen(userId: string, phan: Record<string, KhoiNen | undefined>) {
  queryClient.setQueryData<GoiNen>(khoaNen(userId), (g) => (g ? vaGoi(g, phan) : g));
}

// ---------------------------------------------------------------------
// Tự cập nhật nhẹ (0350 — Le chốt 24/10/2026: nhịp 90 giây). Tab đang HIỆN ⇒ mỗi 90 giây hỏi máy chủ "từ lúc tải gần nhất dữ liệu
// nền có đổi không" (trả ~0,1 KB). Có đổi / sang ngày mới ⇒ mới tải phần đổi (rpc_tai_nen(since) như khi bấm vào cửa sổ).
// Tab ẩn / thu nhỏ ⇒ không hỏi. Quay lại cửa sổ ⇒ refetchOnWindowFocus vẫn làm mới như cũ.
// ---------------------------------------------------------------------
export const NHIP_TU_CAP_NHAT_MS = 90_000;
/** Mốc hỏi lùi 1 phút trước lúc tải (dòng của transaction ghi trước lúc commit vẫn được tính) — đủ nhỏ để không hỏi trúng mãi 1 thay đổi cũ. */
const LUI_HOI_MS = 60_000;

export function useTuCapNhatNen(userId: string) {
  useEffect(() => {
    let dangHoi = false;
    const hoi = async () => {
      if (dangHoi || document.visibilityState !== "visible") return;
      const cu = queryClient.getQueryData<GoiNen>(khoaNen(userId));
      if (!cu?.luc || queryClient.isFetching({ queryKey: khoaNen(userId) })) return;
      dangHoi = true;
      try {
        const since = new Date(new Date(cu.luc).getTime() - LUI_HOI_MS).toISOString();
        const k = await goiDoc<{ doi: boolean; hom_nay: string; may_chu_ms?: number }>("rpc_nen_co_doi", { p_since: since }, "khoi_dong");
        if (k.ok && (k.data.doi || k.data.hom_nay !== cu.hom_nay)) void queryClient.invalidateQueries({ queryKey: khoaNen(userId) });
      } finally { dangHoi = false; }
    };
    const t = setInterval(() => void hoi(), NHIP_TU_CAP_NHAT_MS);
    return () => clearInterval(t);
  }, [userId]);
}

